import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * Minimap — horizontal breadcrumb strip below the editor header.
 *
 * Renders the journey from Intake -> Pre-Auth -> identity_lookup -> ...
 * -> Allow as a horizontal scrolling row of crumbs separated by `/`.
 * Each item carries a small colored dot (the stage accent) and an
 * uppercase mono label. `sys` items render italicised in a lower
 * contrast for "system" steps; `terminal` items mark end states.
 *
 * Source of truth: `mockups/unified-pipeline.html` `.minimap`.
 */
export type MinimapItemType = 'stage' | 'sys' | 'terminal'

export interface MinimapItem {
  /** Crumb label (rendered uppercase by the component). */
  label: string
  /** stage / sys / terminal — controls visual treatment. */
  type: MinimapItemType
  /** Optional accent — defaults sensibly by type. Pass a CSS color
   *  or a `var(--pe-accent-*)` reference. */
  accent?: string
  /** Highlight as the current stop. */
  active?: boolean
  /** Click handler — when present, the crumb is keyboard-focusable. */
  onClick?: () => void
}

export interface MinimapProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Items rendered left-to-right, separated by `/`. */
  items: MinimapItem[]
}

const SEPARATOR = '/'

function defaultAccent(type: MinimapItemType): string {
  switch (type) {
    case 'sys':
      return 'var(--pe-text-quiet)'
    case 'terminal':
      return 'var(--pe-accent-terminal)'
    case 'stage':
    default:
      return 'var(--pe-text-secondary)'
  }
}

export const Minimap = React.forwardRef<HTMLDivElement, MinimapProps>(
  function Minimap({ className, items, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-3.5 px-7 h-10 border-b overflow-x-auto whitespace-nowrap',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          className,
        )}
        style={{
          background: 'color-mix(in srgb, var(--pe-bg-canvas) 80%, transparent)',
          borderColor: 'var(--pe-border-subtle)',
        }}
        // tabIndex makes the horizontally-scrollable strip reachable via
        // keyboard — required by axe `scrollable-region-focusable`.
        tabIndex={0}
        data-testid="pipeline-minimap"
        role="navigation"
        aria-label="Pipeline stages"
        {...rest}
      >
        {items.map((item, idx) => {
          const accent = item.accent ?? defaultAccent(item.type)
          const baseColor =
            item.type === 'sys'
              ? 'var(--pe-text-quiet)'
              : item.active
                ? 'var(--pe-text-primary)'
                : 'var(--pe-text-tertiary)'
          const Tag = item.onClick ? 'button' : 'span'
          return (
            <React.Fragment key={`${item.label}-${idx}`}>
              <Tag
                type={item.onClick ? 'button' : undefined}
                onClick={item.onClick}
                className={cn(
                  'inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.08em] cursor-default transition-colors',
                  item.type === 'sys' && 'italic',
                  item.onClick && 'cursor-pointer hover:[color:var(--pe-text-secondary)]',
                  'bg-transparent border-0 p-0 m-0',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                )}
                style={{ color: baseColor }}
                aria-current={item.active ? 'step' : undefined}
                data-testid="pipeline-minimap-crumb"
                data-active={item.active ? 'true' : undefined}
                data-type={item.type}
              >
                <span
                  aria-hidden
                  className="w-[7px] h-[7px] rounded-[2px]"
                  style={{ background: accent }}
                />
                {item.label}
              </Tag>
              {idx < items.length - 1 && (
                <span
                  className="font-mono text-[10px]"
                  style={{ color: 'var(--pe-text-muted)' }}
                  aria-hidden
                >
                  {SEPARATOR}
                </span>
              )}
            </React.Fragment>
          )
        })}
      </div>
    )
  },
)
